// @ts-strict-ignore
import type { QueryResult } from "@apollo/client";
import { type SyntheticEvent, useEffect, useRef, useState } from "react";

import type {
  MegaMenuData,
  Variables,
} from "@clinic/shared/data/queries/CatalogMegaMenuQuery/CatalogMegaMenu.query";
import { useWordpressSpecialsQuery } from "@emerson/data/queries/WordpressSpecialsQuery/WordpressSpecialsQuery.query";
import { useControlledState } from "@shared/hooks/useControlledState/useControlledState";
import type { MenuCategory } from "@shared/types/graphqlGenerated";
import { isE2E } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import { ShopMenu } from "../ShopMenu/ShopMenu";

type Props = {
  queryResult: QueryResult<MegaMenuData, Variables>;
};

const Content = (props: Props) => {
  const [showCategory, setShowCategory] = useState<MenuCategory>(null);
  const [showSpecialContent, setShowSpecialContent] = useState(false);

  const [mouseOverSideMenu, setMouseOverSideMenu] = useState<boolean>(false);
  const [mouseOverTrigger, setMouseOverTrigger] = useState<boolean>(false);
  const [triggerFocused, setTriggerFocused] = useState<boolean>(false);
  const [triggerElement, setTriggerElement] = useState<Element>(null);
  const [hoverClosed, setHoverClosed] = useState<boolean>(false);
  const [isSideMenuOpen, setIsSideMenuOpen] = useControlledState(false);

  const timeoutRef = useRef(null);
  const triggerRef = useRef(null);
  const menuSwitchTimeoutRef = useRef(null);

  const { data: currentSpecials, loading: loadingSpecials } = useWordpressSpecialsQuery({
    skip: isE2E(),
  });

  const showSpecials = !loadingSpecials && currentSpecials?.specials?.nodes.length > 0;
  const specials = !loadingSpecials ? currentSpecials?.specials?.nodes : [];

  const onMouseEnterMenuItemHandler = e => {
    setMouseOverTrigger(true);
    if (e) setTriggerElement(e.currentTarget);
  };

  const triggerCallbacks = {
    onMouseEnter: (e, menuCategory: MenuCategory) => {
      clearTimeout(menuSwitchTimeoutRef.current);

      if (isSideMenuOpen) {
        // Delay opening another side menu if one is already open
        menuSwitchTimeoutRef.current = setTimeout(() => {
          setShowSpecialContent(false);
          setShowCategory(menuCategory);
        }, 100);
      } else {
        // If a side menu is not already open, open immediately
        setShowSpecialContent(false);
        setShowCategory(menuCategory);
      }

      onMouseEnterMenuItemHandler(e);
    },
    onMouseLeave: () => {
      setMouseOverTrigger(false);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        event.stopPropagation();
        if (isSideMenuOpen) {
          setTriggerFocused(false);
          setHoverClosed(true);
        }
        setIsSideMenuOpen(!isSideMenuOpen);
        if (event) setTriggerElement(event.currentTarget);
      } else if (event.key === "Escape" && isSideMenuOpen) {
        setIsSideMenuOpen(false);
      }
    },
    onFocus: (menuCategory: MenuCategory) => {
      setShowSpecialContent(false);
      setShowCategory(menuCategory);
      setTriggerFocused(true);
    },
    onBlur: () => {
      setTriggerFocused(false);
    },
    onClick: (e: SyntheticEvent) => {
      e.stopPropagation();
      if (isSideMenuOpen) {
        setTriggerFocused(false);
        setHoverClosed(true);
        setShowCategory(null);
        setShowSpecialContent(false);
      }
      setIsSideMenuOpen(!isSideMenuOpen);
      if (e) setTriggerElement(e.currentTarget);
    },
  };

  const sideMenuCallbacks = {
    onMouseEnter: () => {
      setMouseOverSideMenu(true);

      // Prevent the side menu from switching if the user moves
      // the cursor into the current side menu.
      clearTimeout(menuSwitchTimeoutRef.current);
    },
    onMouseLeave: () => {
      setMouseOverSideMenu(false);
    },
    onFocus: () => {
      setTriggerFocused(true);
    },
    onBlur: () => {
      setTriggerFocused(false);
    },
  };

  const specialMenuItemCallbacks = {
    onMouseEnter: e => {
      setShowSpecialContent(true);
      setShowCategory(null);
      onMouseEnterMenuItemHandler(e);
    },
    onFocus: () => {
      setShowSpecialContent(true);
      setTriggerFocused(true);
    },
  };

  useEffect(() => {
    if (isSideMenuOpen && !triggerElement) {
      setTriggerElement(triggerRef.current);
    }
  }, []);

  useEffect(() => {
    if ((mouseOverTrigger || triggerFocused) && !hoverClosed) {
      setIsSideMenuOpen(true);
      clearTimeout(timeoutRef.current);
    } else if (isSideMenuOpen && !mouseOverSideMenu) {
      timeoutRef.current = setTimeout(() => {
        setIsSideMenuOpen(false);
        setShowSpecialContent(false);
        setShowCategory(null);
      }, 100);
    }
  }, [mouseOverSideMenu, mouseOverTrigger, triggerFocused]);

  useEffect(() => {
    if (hoverClosed && !mouseOverTrigger) {
      setHoverClosed(false);
    }
  }, [mouseOverTrigger]);

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(menuSwitchTimeoutRef.current);
    };
  }, []);

  return (
    <ShopMenu
      {...props}
      showCategory={showCategory}
      isSideMenuOpen={isSideMenuOpen}
      showSpecialContent={showSpecialContent}
      sideMenuCallbacks={sideMenuCallbacks}
      specialMenuItemCallbacks={specialMenuItemCallbacks}
      triggerCallbacks={triggerCallbacks}
      specialsData={{ specials, showSpecials }}
      ref={triggerRef}
    />
  );
};

export { Content };
